import { useState } from 'react'
import {
  type TimePreset, type TimeRange, type Granularity,
  getRange, PRESET_LABELS, ALL_PRESETS, GRAN_LABELS, ALL_GRANS,
} from '../lib/time-filter'

interface Props {
  value: TimeRange
  onChange: (r: TimeRange) => void
}

export function TimeFilter({ value, onChange }: Props) {
  const [localFrom, setLocalFrom] = useState(value.from)
  const [localTo,   setLocalTo]   = useState(value.to)

  // When a preset button is clicked: update dates + auto-set granularity
  const applyPreset = (p: TimePreset) => {
    const r = getRange(p)
    setLocalFrom(r.from)
    setLocalTo(r.to)
    onChange(r)
  }

  // When Apply is clicked after manually editing dates
  const applyCustom = () => {
    onChange(getRange('custom', localFrom, localTo, value.granularity))
  }

  // When granularity toggle changes (keep dates as-is)
  const applyGran = (g: Granularity) => {
    onChange({ ...value, granularity: g })
  }

  const datesChanged = localFrom !== value.from || localTo !== value.to

  return (
    <div className="flex flex-col gap-1 mb-2">
      {/* Preset shortcuts */}
      <div className="flex flex-wrap items-center gap-1">
        {ALL_PRESETS.map(p => (
          <button
            key={p}
            onClick={() => applyPreset(p)}
            className={[
              'text-[10px] px-1.5 py-0.5 rounded border transition-colors whitespace-nowrap leading-none',
              value.preset === p
                ? 'bg-royal-600 text-white border-royal-600'
                : 'text-ink-400 border-ink-200 hover:border-royal-500 hover:text-royal-600',
            ].join(' ')}
          >
            {PRESET_LABELS[p]}
          </button>
        ))}
      </div>

      {/* Always-visible date range */}
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={localFrom}
          max={localTo}
          onChange={e => {
            setLocalFrom(e.target.value)
            if (localTo && e.target.value > localTo) setLocalTo('')
          }}
          className="text-[10px] border border-ink-200 rounded px-1.5 py-0.5 text-ink-700 bg-white"
          style={{ width: 118 }}
        />
        <span className="text-[10px] text-ink-400">–</span>
        <input
          type="date"
          value={localTo}
          min={localFrom}
          onChange={e => {
            setLocalTo(e.target.value)
            if (localFrom && e.target.value < localFrom) setLocalFrom('')
          }}
          className="text-[10px] border border-ink-200 rounded px-1.5 py-0.5 text-ink-700 bg-white"
          style={{ width: 118 }}
        />
        <button
          onClick={applyCustom}
          disabled={!datesChanged}
          className={[
            'text-[10px] px-2 py-0.5 rounded whitespace-nowrap transition-colors',
            datesChanged
              ? 'bg-royal-600 text-white hover:bg-royal-700'
              : 'bg-ink-100 text-ink-400 cursor-default',
          ].join(' ')}
        >
          Apply
        </button>

        {/* Granularity toggle */}
        <div className="flex items-center gap-0.5 ml-2">
          {ALL_GRANS.map(g => (
            <button
              key={g}
              onClick={() => applyGran(g)}
              className={[
                'text-[10px] px-1.5 py-0.5 rounded border transition-colors leading-none',
                value.granularity === g
                  ? 'bg-ink-700 text-white border-ink-700'
                  : 'text-ink-400 border-ink-200 hover:border-ink-400 hover:text-ink-600',
              ].join(' ')}
            >
              {GRAN_LABELS[g]}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
